import { memo, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  ScrollArea,
} from '@flote/ui';
import { TaskForm, type TaskFormData } from './TaskForm';
import type { Team, Recipient, Destination, B2BWaveOrderSummary, Driver } from '@flote/contracts/http';

interface TaskFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: TaskFormData) => Promise<void>;
  isProcessing?: boolean;
  teams: Team[];
  recipients: Recipient[];
  destinations: Destination[];
  orders?: B2BWaveOrderSummary[];
  onFetchDriversByTeam?: (teamId: string) => Promise<Driver[]>;
}

export const TaskFormModal = memo<TaskFormModalProps>(({
  open,
  onOpenChange,
  onSubmit,
  isProcessing = false,
  teams,
  recipients,
  destinations,
  orders = [],
  onFetchDriversByTeam,
}) => {
  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (!newOpen && !isProcessing) {
        onOpenChange(false);
      }
    },
    [onOpenChange, isProcessing]
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-2xl max-h-[90vh] p-0 sm:w-full">
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle>Create Delivery Task</DialogTitle>
          <DialogDescription>
            Create a new task for Onfleet delivery management
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[calc(90vh-120px)]">
          <div className="px-6 pb-6">
            <TaskForm
              onSubmit={onSubmit}
              isProcessing={isProcessing}
              teams={teams}
              recipients={recipients}
              destinations={destinations}
              orders={orders}
              onFetchDriversByTeam={onFetchDriversByTeam}
            />
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
});

TaskFormModal.displayName = 'TaskFormModal';
